import COLORS from "common/Colors";
import styled from "styled-components";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import React, { useContext } from "react";
import { ContainerContext } from "pages/Container";
import { OrganizationContext, calculateNumberOfReportees } from ".";
import { ReactComponent as ChatIcon } from "assets/chat.svg";
import { ReactComponent as EmailIcon } from "assets/email.svg";
import { Popover } from "antd";
import { TOPGRADESUPERVISORLIST } from "common/Constants";

export const Card = ({ person, linked, subordinates = [], team, type, filteruser = [], isAboveLoggedInUser = false, isSideTeammate = false, hideCountBadge = false, isLoggedInUser = false }) => {
  const {
    setActivePerson,
    organizationLevels,
    findEmployeeById,
    setOrganization,
    organization,
    activePerson,
    loggedInEmployee,
    userListData,
  } = React.useContext(OrganizationContext);

  const { layoutContentRef } = useContext(ContainerContext);

  const history = useHistory();

  const handleReporteesClick = (personId) => {
    try {
      if (!personId || !organizationLevels?.length) return;

      // ✅ MY TEAM VIEW - Special handling for logged-in user (show teammates)
      if (team && isLoggedInUser && person.teammatesCount > 0) {
        // When clicking logged-in user's badge, show all teammates
        const levelObj = organizationLevels.find((level) =>
          Array.isArray(level.people) &&
          level.people.some((p) =>
            typeof p === "object" ? p.id === personId : p === personId
          )
        );

        if (!levelObj) return;

        const personLevelIndex = organizationLevels.findIndex(
          (level) => level.levelid === levelObj.levelid
        );

        // Don't navigate, just highlight - teammates are already visible
        return;
      }

      // ✅ MY TEAM VIEW - Prevent clicking on non-subordinates
      if (team && hideCountBadge && !isLoggedInUser) {
        return; // Don't allow clicking if badge is hidden
      }

      const levelObj = organizationLevels.find((level) =>
        Array.isArray(level.people) &&
        level.people.some((p) =>
          typeof p === "object" ? p.id === personId : p === personId
        )
      );

      if (!levelObj) return;

      const personLevelIndex = organizationLevels.findIndex(
        (level) => level.levelid === levelObj.levelid
      );

      const employee = findEmployeeById?.(personId);
      if (!employee?.reportees?.length) return;

      // ✅ Update activePerson for the clicked level
      const newActivePerson = [...activePerson];
      newActivePerson[personLevelIndex] = {
        ...newActivePerson[personLevelIndex],
        activePerson: employee,
      };

      // ✅ Reorder current level to put clicked person first
      const newOrganization = [...organization];
      const currentLevelPeople = [...organization[personLevelIndex].people];
      const clickedPersonIndex = currentLevelPeople.findIndex(p => p.id === personId);
      
      if (clickedPersonIndex > 0) {
        const clickedPerson = currentLevelPeople.splice(clickedPersonIndex, 1)[0];
        currentLevelPeople.unshift(clickedPerson);
        
        newOrganization[personLevelIndex] = {
          ...newOrganization[personLevelIndex],
          people: currentLevelPeople,
        };
      }

      // ✅ Update the next level to show this person's reportees and expand all levels below
      let currentLevelIndex = personLevelIndex + 1;
      let currentParent = employee;

      while (currentLevelIndex < organization.length && currentParent?.reportees?.length > 0) {
        const reporteeData = currentParent.reportees.map((reporteeId) => {
          const reportee = findEmployeeById(reporteeId);
          const numberOfReportees = calculateNumberOfReportees(reportee);
          return {
            ...reportee,
            numberOfReportees,
          };
        });

        newOrganization[currentLevelIndex] = {
          ...newOrganization[currentLevelIndex],
          people: reporteeData,
        };

        newActivePerson[currentLevelIndex] = {
          levelid: newOrganization[currentLevelIndex].levelid,
          activePerson: reporteeData[0] || null,
          people: reporteeData,
        };

        currentParent = reporteeData[0] || null;
        currentLevelIndex++;
      }

      // ✅ Clear any remaining levels that weren't filled
      for (let i = currentLevelIndex; i < newOrganization.length; i++) {
        newOrganization[i] = {
          ...newOrganization[i],
          people: [],
        };
        newActivePerson[i] = {
          levelid: newOrganization[i].levelid,
          activePerson: null,
          people: [],
        };
      }

      setOrganization(newOrganization);
      setActivePerson(newActivePerson);

      if (layoutContentRef?.current) {
        layoutContentRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    } catch (err) {
      console.error("handleReporteesClick error:", err);
    }
  };

  const handleEmailClick = (id) => {
    window.open(`mailto:${id}`);
  };

  // ✅ View button logic
  const shouldShowViewButton = () => {
    if (!team) return true; // Always show for People menu
    
    // For My Team: Show only for subordinates (people below logged-in user)
    // hideCountBadge is false only for subordinates
    return !hideCountBadge;
  };

  return (
    <CardWrap>
      <div className="card">
        <div className="card-layout">
          <div className="card-main">
            {person.profileurl ? (
              <img className="card-img" src={person.profileurl} alt="" />
            ) : (
              <>
                <div className="card-img person-name">{person.name?.[0]}</div>
              </>
            )}
            {!team && (
              <div className="card-text">
                <Popover title={person.name} content={false}>
                  <div className="name">{person.name}</div>
                </Popover>
                <div className="role">{person.grade}</div>
              </div>
            )}
            {team && type === "office" && (
              <div className="card-text">
                <Popover title={person.name} content={false}>
                  <div className="name"> {person.name}</div>
                </Popover>
                <div className="role">
                  Designation: {person.designation ? person.designation : "-"}
                </div>
                <div className="role">
                  Department:{" "}
                  {person?.leveldepartmentname?.length > 0
                    ? person.leveldepartmentname?.join(", ")
                    : "-"}
                </div>
                <div className="email">
                  Email ID:{" "}
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={() => handleEmailClick(person.userid)}
                  >
                    {person.userid}
                  </span>
                </div>
              </div>
            )}
            {team && type === "regional" && (
              <div className="card-text">
                <Popover title={person.name} content={false}>
                  <div className="name">{person.name}</div>
                </Popover>
                <div className="role">
                  Department:
                  {person?.leveldepartmentname?.length > 0
                    ? person.leveldepartmentname?.join(", ")
                    : "-"}
                </div>
                <div className="email">
                  Email ID:{" "}
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={() => handleEmailClick(person.userid)}
                  >
                    {person.userid}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="card-footer">
            <div className="buttons">
              <div  className="chat"
              style={{ 
                visibility: shouldShowViewButton() ? "visible" : "hidden",
                pointerEvents: shouldShowViewButton() ? "auto" : "none"
              }}
            >
              <ChatIcon />
            </div>
              {shouldShowViewButton() && (
                <div className="email"  style={{ 
                  visibility: shouldShowViewButton() ? "visible" : "hidden",
                  pointerEvents: shouldShowViewButton() ? "auto" : "none"
                }}>
                  <EmailIcon
                    style={{ cursor: "pointer" }}
                    onClick={() => handleEmailClick(person.userid)}
                  />
                </div>
              )}
            </div>
            {shouldShowViewButton() && team && (
              <div
                className="view"
                onClick={() => {
                  history.push(`/profile-details/${person.userid}/?type=team`);
                }}
              >
                View{" "}
              </div>
            )}
            {!team && (
              <div
                className="view"
                onClick={() => {
                  history.push(
                    `/profile-details/${person.userid}/?type=manage`
                  );
                }}
              >
                View{" "}
              </div>
            )}
          </div>
        </div>
      </div>
      {!hideCountBadge && (
        <div
          onClick={() => handleReporteesClick(person.id)}
          className={`box ${linked ? "linked" : ""} ${isLoggedInUser ? "linked" : ""}`}
        >
          {isLoggedInUser && person.teammatesCount !== undefined 
            ? person.teammatesCount 
            : person.numberOfReportees || 0}
        </div>
      )}
    </CardWrap>
  );
};

const CardWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  .card {
    background-color: ${COLORS.white};
    border-radius: 5px;
    border: 1px solid #b5b5b5;
    width: 300px;

    .card-layout {
      display: flex;
      flex-direction: column;
      height: 100%;
      .card-main {
        display: flex;
        align-items: start;
        gap: 0.5rem;
        padding: 0.5rem;
        .card-img {
          width: 55px;
          height: 55px;

          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 20px;
          background-color: #b5b5b5;
          border-radius: 5px;
          color: white;
        }
        .card-text {
          line-height: 1.4;
          .name {
            font-size: 14px;
            font-weight: 650;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            width: 130px;
          }
          .email {
            font-size: 12px;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            width: 200px;
          }
          .role {
            font-size: 12px;
            font-weight: 500;
          }
        }
      }
      .card-footer {
        flex-grow: 1;
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        padding: 0.4rem;
        background-color: ${COLORS.PRIMARY};
        border-radius: 0 0 5px 5px;
        .buttons {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          .chat,
          .email {
            padding-right: 10px;
            border-right: 0.5px solid #575757;
            cursor: pointer;
          }
        }
        .view {
          font-size: 12px;
          text-decoration: underline;
          font-weight: 600;
          cursor: pointer;
        }
      }
    }
  }

  .box {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 28px;
    color: white;
    background-color: #b5b5b5;
    border-radius: 5px;
    position: relative;
    cursor: pointer;

    &::before {
      content: "";
      z-index: 1;
      position: absolute;
      bottom: 100%;
      height: 1rem;
      width: 1.5px;
      background-color: #b5b5b5;
    }

    &.non-clickable {
      cursor: default;
      opacity: 0.6;
    }

    &.linked {
      background-color: ${COLORS.BLUE};

      &::before {
        background-color: ${COLORS.BLUE};
      }
      &::after {
        content: "";
        z-index: 1;
        position: absolute;
        top: 100%;
        height: 1rem;
        width: 1.5px;
        background-color: ${COLORS.BLUE};
      }
    }
  }
`;